import { clsx, type ClassValue } from "clsx";

export const cn = (...inputs: ClassValue[]) => clsx(inputs);

export const formatDate = (value: Date | string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));

export const formatDateTime = (value: Date | string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );

export const shortId = (id: string | null | undefined) => (id ? id.slice(-8) : "—");

/**
 * SEC-19: only same-origin paths may be used as post-sign-in redirects.
 * The value is parsed as a URL (not prefix-checked), because browsers strip tabs
 * and newlines and treat "\\" like "/", so "/\t/evil.example" or "/\\evil.example"
 * would otherwise become a protocol-relative URL to another site.
 */
const hasControlOrBackslash = (value: string) => {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f || code === 0x5c) return true; // control chars, DEL, "\\"
  }
  return false;
};

export const safeRedirect = (target: unknown, fallback = "/"): string => {
  if (typeof target !== "string" || !target.startsWith("/") || hasControlOrBackslash(target))
    return fallback;
  try {
    const url = new URL(target, window.location.origin);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : fallback;
  } catch {
    return fallback;
  }
};

/** A calendar date ("2026-11-30", no time zone): shown as that day everywhere. */
export const formatCalendarDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeZone: "UTC" }).format(
    new Date(`${value}T00:00:00Z`),
  );
